import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/api";
import JobCard from "@/components/JobCard";
import CreateJobDialog from "@/components/CreateJobDialog";
import SendWeeklyScheduleDialog from "@/components/SendWeeklyScheduleDialog";
import { Plus, CheckCircle2, ClipboardList, Search, X, Download, Send, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PushToggleCard from "@/components/PushToggleCard";
import DaySummariesPanel from "@/components/DaySummariesPanel";

export default function ManagerDashboard() {
    const { user } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [operatives, setOperatives] = useState([]);
    const [clientsById, setClientsById] = useState({});
    const [stats, setStats] = useState(null);
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [downloadingArchive, setDownloadingArchive] = useState(false);
    const [archiveStart, setArchiveStart] = useState("");
    const [archiveEnd, setArchiveEnd] = useState("");
    const [sendWeeklyOpen, setSendWeeklyOpen] = useState(false);

    // Jobs completed >7 days ago that still haven't been finalised. Every
    // dashboard load counts these and, if there are any, we surface a
    // reminder banner so paperwork doesn't pile up. Only shows on the
    // "All jobs" tab (a persistent banner on the Completed tab itself
    // would be noise — that tab is where you go to see them).
    const staleAwaitingFinalisation = useMemo(() => {
        const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        return jobs.filter(j => j.status === "completed" && new Date(j.scheduled_at).getTime() < cutoff);
    }, [jobs]);

    const downloadArchiveCsv = async () => {
        setDownloadingArchive(true);
        try {
            const params = {};
            if (archiveStart) params.start = archiveStart;
            if (archiveEnd) params.end = archiveEnd;
            const res = await api.get("/archive/export.csv", { responseType: "blob", params });
            const blob = new Blob([res.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const suffix = archiveStart || archiveEnd
                ? `${archiveStart || "start"}_to_${archiveEnd || new Date().toISOString().slice(0,10)}`
                : new Date().toISOString().slice(0, 10);
            a.download = `scs_archive_${suffix}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Archive CSV downloaded");
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't build archive CSV");
        } finally { setDownloadingArchive(false); }
    };

    const load = async () => {
        try {
            const [j, u, s, c] = await Promise.all([
                api.get("/jobs"),
                api.get("/users"),
                api.get("/stats/manager"),
                api.get("/clients"),
            ]);
            setJobs(j.data);
            setOperatives(u.data);
            setStats(s.data);
            const map = {};
            for (const cl of c.data) map[cl.id] = cl;
            setClientsById(map);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const now = Date.now();
        const ARCHIVE_MS = 30 * 24 * 60 * 60 * 1000;
        const isArchived = (j) => j.status === "completed" && (now - new Date(j.scheduled_at).getTime()) > ARCHIVE_MS;

        // Recurring occurrences live on the Schedule tab — hide them from
        // the dashboard everywhere so it stays focused on one-off actions.
        // Search still finds them (the query branch below ignores this rule)
        // so a rescheduled recurring job is still reachable via search.
        const isRecurring = (j) => Boolean(j.recurrence_kind || (j.recurrence_days && j.recurrence_days > 0));

        const q = query.trim().toLowerCase();

        // When the manager types a query, ignore the current filter tab and search
        // ACROSS ALL JOBS (including completed + archived). This is what people
        // instinctively expect — "if it's in the system, find it".
        let list = jobs;
        if (!q) {
            if (filter === "unassigned") list = list.filter(j => !j.assigned_to && j.status !== "completed" && j.status !== "finalised" && !isArchived(j));
            else if (filter === "all") list = list.filter(j => (
                j.status !== "completed"
                && j.status !== "finalised"
                && !isArchived(j)
                && !isRecurring(j)
            ));
            else if (filter === "completed") list = list.filter(j => j.status === "completed" && !isArchived(j));
            else if (filter === "finalised") list = list.filter(j => j.status === "finalised" && !isArchived(j));
            else if (filter === "archive") list = list.filter(isArchived);
            else if (filter === "summaries") list = [];
            else list = list.filter(j => (
                j.status === filter
                && !isArchived(j)
                && !isRecurring(j)
            ));
            return list;
        }

        return list.filter(j => {
            const c = clientsById[j.client_id] || {};
            const haystack = [
                j.title,
                j.description,
                j.notes,
                j.address,        // job's own address (often typed without a client dossier)
                j.client_name,
                j.assigned_to_name,
                (j.helper_names || []).join(" "),
                j.status,
                (j.checklist || []).join(" "),
                c.address,
                c.gate_code,
                c.alarm_code,
                c.key_location,
                c.access_notes,
                c.parking_notes,
                c.contact_name,
                c.contact_phone,
                c.contact_email,
            ].filter(Boolean).join(" ").toLowerCase();
            return haystack.includes(q);
        });
    }, [jobs, filter, query, clientsById]);

    return (
        <div className="space-y-8">
            {/* Header */}
            <section className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="text-xs uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">Manager view</span>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="dashboard-heading">
                        Good {greeting()}, {user?.name?.split(" ")[0]}
                    </h1>
                    <p className="text-[var(--text-secondary)] mt-1 text-sm">Here&apos;s what&apos;s on the schedule.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button data-testid="send-weekly-btn" onClick={() => setSendWeeklyOpen(true)}
                            className="btn-ghost inline-flex items-center gap-2">
                        <Send size={14} /> Send weekly schedule
                    </button>
                    <button data-testid="new-job-btn" onClick={() => setDialogOpen(true)}
                            className="btn-primary inline-flex items-center gap-2">
                        <Plus size={16} /> New job
                    </button>
                </div>
            </section>

            {/* Stats bento */}
            <section className="grid grid-cols-2 md:grid-cols-2 gap-3 sm:gap-5 md:max-w-xl" data-testid="stats-grid">
                <StatCard icon={<ClipboardList size={18} />} label="Jobs" value={jobs.filter(j => j.status !== "completed" && j.status !== "finalised").length ?? "—"} tint="var(--pine-light)" fg="var(--pine)" />
                <StatCard icon={<CheckCircle2 size={18} />} label="Completed" value={jobs.filter(j => j.status === "completed" || j.status === "finalised").length ?? "—"} tint="rgba(46,125,50,0.12)" fg="var(--success)" />
            </section>

            {/* Search */}
            <section className="relative" data-testid="search-bar">
                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none" />
                <input
                    data-testid="job-search-input"
                    type="search"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search jobs, clients, addresses, gate codes, operatives…"
                    className="w-full rounded-full pl-10 pr-10 py-3 text-sm bg-white border border-[var(--border-soft)] focus:outline-none focus:ring-2 focus:ring-[var(--pine)]/30 focus:border-[var(--pine)]"
                />
                {query && (
                    <button
                        data-testid="clear-search-btn"
                        onClick={() => setQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-[var(--text-muted)] hover:text-black hover:bg-[var(--surface-soft)]"
                        aria-label="Clear search">
                        <X size={14} />
                    </button>
                )}
                {query.trim() && (
                    <div
                        data-testid="search-scope-hint"
                        className="mt-2 flex items-center gap-2 text-xs text-[var(--text-muted)]">
                        <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-soft)] px-2.5 py-1 font-medium text-[var(--text-secondary)]">
                            <Search size={11} /> {filtered.length} match{filtered.length === 1 ? "" : "es"} across every tab
                        </span>
                        <span>— including completed & archive</span>
                    </div>
                )}
            </section>

            {/* Awaiting-finalisation reminder — only when there's action to take
                and only outside search mode. Clicking the banner jumps to the
                Completed tab where the stale jobs live so the manager can
                finalise them in one place. */}
            {!query.trim() && filter !== "completed" && staleAwaitingFinalisation.length > 0 && (
                <button
                    data-testid="finalisation-reminder"
                    onClick={() => setFilter("completed")}
                    className="w-full text-left flex items-center gap-3 rounded-2xl border border-[var(--terracotta)]/40 bg-[var(--terracotta-light)] px-4 py-3 hover:border-[var(--terracotta)] transition-colors">
                    <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
                         style={{ background: "var(--terracotta)", color: "white" }}>
                        <AlertCircle size={17} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-[var(--text-primary)]">
                            {staleAwaitingFinalisation.length} completed
                            {" "}
                            {staleAwaitingFinalisation.length === 1 ? "job" : "jobs"}
                            {" "}
                            older than a week — waiting to be finalised
                        </div>
                        <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                            Tap to review and finalise them so paperwork stays tidy.
                        </div>
                    </div>
                    <span className="text-xs font-semibold text-[var(--terracotta)] shrink-0 pr-1">View →</span>
                </button>
            )}

            {/* Filters */}
            <section className="flex flex-wrap gap-2" data-testid="filter-bar">
                {[
                    { k: "all", label: "All jobs" },
                    { k: "scheduled", label: "Scheduled" },
                    { k: "in_progress", label: "In progress" },
                    { k: "completed", label: "Completed" },
                    { k: "finalised", label: "Finalised" },
                    { k: "unassigned", label: "Unassigned" },
                    { k: "archive", label: "Archive · 30+ days" },
                    { k: "summaries", label: "Day summaries" },
                ].map(f => (
                    <button key={f.k}
                            data-testid={`filter-${f.k}`}
                            onClick={() => setFilter(f.k)}
                            className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
                                filter === f.k
                                    ? "bg-[var(--pine)] text-white border-[var(--pine)]"
                                    : "bg-white border-[var(--border-soft)] text-[var(--text-secondary)] hover:border-[var(--pine)]"
                            }`}>
                        {f.label}
                    </button>
                ))}
            </section>

            {filter === "archive" && (
                <section
                    className="card-flat p-4 space-y-3"
                    data-testid="archive-export-panel"
                >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="font-display text-base font-semibold">Annual records</p>
                            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                                Every completed job with clock in/out, hours and client details. Leave dates blank for the default 30-day+ archive.
                            </p>
                        </div>
                        <button
                            data-testid="download-archive-csv-btn"
                            onClick={downloadArchiveCsv}
                            disabled={downloadingArchive}
                            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            <Download size={16} />
                            {downloadingArchive ? "Building CSV…" : "Download archive CSV"}
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                        <div>
                            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-semibold">From (optional)</label>
                            <input
                                data-testid="archive-start-input"
                                type="date"
                                value={archiveStart}
                                onChange={e => setArchiveStart(e.target.value)}
                                max={archiveEnd || undefined}
                                className="input-field mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-muted)] font-semibold">To (optional)</label>
                            <input
                                data-testid="archive-end-input"
                                type="date"
                                value={archiveEnd}
                                onChange={e => setArchiveEnd(e.target.value)}
                                min={archiveStart || undefined}
                                className="input-field mt-1"
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                data-testid="archive-preset-year"
                                onClick={() => {
                                    const y = new Date().getFullYear();
                                    setArchiveStart(`${y}-01-01`);
                                    setArchiveEnd(`${y}-12-31`);
                                }}
                                className="btn-ghost text-xs !px-3 !py-1.5"
                            >
                                This year
                            </button>
                            <button
                                type="button"
                                data-testid="archive-preset-clear"
                                onClick={() => { setArchiveStart(""); setArchiveEnd(""); }}
                                className="btn-ghost text-xs !px-3 !py-1.5"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </section>
            )}

            {filter === "summaries" && <DaySummariesPanel />}

            {/* Job list */}
            <section>
                {filter === "summaries" ? null : loading ? (
                    <div className="text-[var(--text-muted)] text-sm">Loading jobs…</div>
                ) : filtered.length === 0 ? (
                    <div className="card-flat p-10 text-center" data-testid="empty-state">
                        {query ? (
                            <>
                                <Search size={26} className="mx-auto text-[var(--text-muted)] opacity-60" />
                                <p className="font-display text-lg font-semibold mt-3">Nothing matched &ldquo;{query}&rdquo;</p>
                                <p className="text-sm text-[var(--text-secondary)] mt-1">Try a shorter word, a street name, or a gate code.</p>
                                <button className="btn-ghost mt-4" onClick={() => setQuery("")}>Clear search</button>
                            </>
                        ) : (
                            <>
                                <p className="font-display text-lg font-semibold">No jobs yet</p>
                                <p className="text-sm text-[var(--text-secondary)] mt-1">Create your first job to get started.</p>
                                <button className="btn-primary mt-4" onClick={() => setDialogOpen(true)}>Create job</button>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        {query && (
                            <div className="mb-3 text-xs text-[var(--text-muted)]" data-testid="search-result-count">
                                {filtered.length} {filtered.length === 1 ? "match" : "matches"} for <b className="text-[var(--text-secondary)]">{query}</b>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="jobs-grid">
                            {filtered.map(j => (
                                <JobCard key={j.id} job={j} clientColor={clientsById[j.client_id]?.color} query={query} onUpdate={(updated) => setJobs(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x))} />
                            ))}
                        </div>
                    </>
                )}
            </section>

            <CreateJobDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onCreated={(j) => { setJobs(prev => [j, ...prev]); load(); }}
                operatives={operatives}
            />

            <SendWeeklyScheduleDialog
                open={sendWeeklyOpen}
                onClose={() => setSendWeeklyOpen(false)}
                members={operatives}
            />

            <PushToggleCard />
        </div>
    );
}

function StatCard({ icon, label, value, tint, fg }) {
    return (
        <div className="card-flat p-5">
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center"
                     style={{ background: tint, color: fg }}>{icon}</div>
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-medium">{label}</div>
                    <div className="font-display text-2xl font-bold leading-none mt-1">{value}</div>
                </div>
            </div>
        </div>
    );
}

function greeting() {
    const h = new Date().getHours();
    if (h < 12) return "morning";
    if (h < 18) return "afternoon";
    return "evening";
}
